import { PREVIEW_PROTOCOL } from "./previewProtocolConstants";
import { getPreviewIdentity } from "./previewIdentity";
import { PRODUCT_ROLE } from "./productRoleConstants";
import { US_STATE_CODES } from "../../constants/usStates";

export const PREVIEW_SIMULATION_DELAY_MS = 500;

export const waitForPreviewSimulation = () => new Promise((resolve) => {
  setTimeout(resolve, PREVIEW_SIMULATION_DELAY_MS);
});

const normalizedStates = (values) => [...new Set(
  (Array.isArray(values) ? values : [])
    .map((value) => String(value || "").trim().toUpperCase())
    .filter((value) => US_STATE_CODES.includes(value)),
)].sort();

const previewPrograms = (questionnaire) => {
  const runtimePrograms = questionnaire?.runtime_availability?.programs || [];
  if (runtimePrograms.length) return runtimePrograms;
  return (questionnaire?.questions || [])
    .filter((question) => question.question_type === "matched_program_selection")
    .flatMap((question) => question.answer_choices || []);
};

const dateOfBirthForAge = (age) => {
  const today = new Date();
  const date = new Date(today.getFullYear() - age, today.getMonth(), today.getDate());
  return date.toISOString().slice(0, 10);
};

const boundedDefault = (defaultValue, minValue, maxValue) => {
  const constraint = (value) => (
    value === null || value === undefined || value === ""
      ? null
      : (Number.isFinite(Number(value)) ? Number(value) : null)
  );
  const min = constraint(minValue);
  const max = constraint(maxValue);
  if (min !== null && max !== null && min > max) return defaultValue;
  if (min !== null && defaultValue < min) return min;
  if (max !== null && defaultValue > max) return max;
  return defaultValue;
};

const productStatesForProgram = (questionnaire, program) => {
  // A Program without an authored Service Area Check is intentionally
  // available in every state; it must not narrow a shared selector used by a
  // different Program that does have a state-routing question.
  if (program?.has_service_area_question === false) return [...US_STATE_CODES];
  const programStates = program?.service_states_all
    ? [...US_STATE_CODES]
    : normalizedStates(program?.service_states);
  const choices = (questionnaire?.questions || [])
    .filter((question) => (
      question.question_type === "product_selection"
      && String(question.program_id || "") === String(program?.id || "")
    ))
    .flatMap((question) => question.answer_choices || []);
  if (!choices.length) return null;
  const available = new Set();
  choices.forEach((choice) => {
    if (
      !choice
      || choice.is_active === false
      || [PRODUCT_ROLE.clinicianOnly, PRODUCT_ROLE.informational].includes(
        choice.product_role,
      )
    ) return;
    const productStates = normalizedStates(
      choice.effective_service_states || choice.service_states,
    );
    const candidates = productStates.length
      ? productStates
      : programStates;
    candidates.forEach((state) => {
      if (!programStates.length || programStates.includes(state)) available.add(state);
    });
  });
  return [...available].sort();
};

/**
 * Return deterministic synthetic demographics for an admin preview.
 *
 * Preview remains an actual runtime session. These defaults only avoid an
 * arbitrary fixed sample (female, 38, FL) making an otherwise valid authored
 * Program look ineligible before the author can inspect its checkout.
 */
export const previewPatientDefaults = (questionnaire) => {
  const program = previewPrograms(questionnaire).find(
    (item) => item?.matching_enabled !== false,
  );
  const eligibility = program?.eligibility || {};
  const runtimeProfile = (
    questionnaire?.runtime_visibility_context?.patient_profile
    || questionnaire?.visibility_context?.patient_profile
    || {}
  );
  const runtimeSex = String(runtimeProfile.sex || "").toLowerCase();
  const sexRequirement = String(eligibility.sex || "").toLowerCase();
  const sex = ["male", "female", "other"].includes(runtimeSex)
    ? runtimeSex
    : ["male", "female", "other"].includes(sexRequirement)
      ? sexRequirement
    : PREVIEW_PROTOCOL.patientDefaults.sex;
  const runtimeAge = Number(runtimeProfile.age);
  const age = boundedDefault(
    Number.isFinite(runtimeAge) ? runtimeAge : 38,
    eligibility.min_age,
    eligibility.max_age,
  );
  const bmi = boundedDefault(
    PREVIEW_PROTOCOL.patientDefaults.bmi,
    eligibility.min_bmi,
    eligibility.max_bmi,
  );
  const availableStates = program
    ? (productStatesForProgram(questionnaire, program)
      || (program.service_states_all
        ? [...US_STATE_CODES]
        : normalizedStates(program.service_states)))
    : [];
  const state = availableStates.includes(PREVIEW_PROTOCOL.patientDefaults.state)
    ? PREVIEW_PROTOCOL.patientDefaults.state
    : availableStates[0] || PREVIEW_PROTOCOL.patientDefaults.state;

  return {
    date_of_birth: dateOfBirthForAge(age),
    sex,
    state,
    bmi,
  };
};

export const createPreviewPatient = (details = {}) => ({
  patient_id: "preview-patient",
  is_new_patient: getPreviewIdentity() === PREVIEW_PROTOCOL.identities.newPatient,
  preview: true,
  email: details.email || "preview@example.com",
  phone: details.phone || "+12125550184",
  firstName: details.firstName || details.first_name || "Preview",
  lastName: details.lastName || details.last_name || "Patient",
  first_name: details.firstName || details.first_name || "Preview",
  last_name: details.lastName || details.last_name || "Patient",
  date_of_birth: details.dateOfBirth
    || details.date_of_birth
    || PREVIEW_PROTOCOL.patientDefaults.dateOfBirth,
  sex: details.sex || PREVIEW_PROTOCOL.patientDefaults.sex,
  state: details.state || PREVIEW_PROTOCOL.patientDefaults.state,
  bmi: details.bmi || PREVIEW_PROTOCOL.patientDefaults.bmi,
});

export const createPreviewPatientPrefill = (patient) => ({
  patient,
  personal_details: {
    first_name: patient.first_name,
    last_name: patient.last_name,
    email: patient.email,
    phone: patient.phone,
    date_of_birth: patient.date_of_birth,
    sex: patient.sex,
    state: patient.state,
    bmi: patient.bmi,
  },
  prefill_responses: {},
  prefill_sources: {},
});

export const previewServiceStates = (questionnaire) => {
  const programs = previewPrograms(questionnaire);
  const states = new Set();
  programs.forEach((program) => {
    const programStates = program.service_states_all === true
      ? [...US_STATE_CODES]
      : normalizedStates(program.service_states);
    const productStates = productStatesForProgram(questionnaire, program);
    // Checkout option availability is the final authority for a state. If
    // the option projection is not present (for example while loading a
    // legacy questionnaire), retain the Program-level state list. An empty
    // option projection is meaningful: it means no configured product covers
    // any state and must not be widened back to the authored list.
    (productStates === null ? programStates : productStates).forEach(
      (state) => states.add(state),
    );
  });
  return [...states].sort();
};

/**
 * Project the two state sets needed by the Service Area Check UI.
 *
 * A Program with an explicit, selected-state scope should let the patient
 * search every US state. Availability remains authoritative and is kept in
 * `servableStates`, so an unsupported selection is rejected instead of being
 * hidden from the patient.
 */
export const previewStateRoutingProjection = (questionnaire) => {
  const programs = previewPrograms(questionnaire);
  if (!programs.length) return null;
  const servableStates = previewServiceStates(questionnaire);
  const hasSelectedStateProgram = programs.some(
    (program) => (
      program?.has_service_area_question !== false
      && program?.service_states_all === false
    ),
  );

  return {
    allStates: hasSelectedStateProgram ? [...US_STATE_CODES] : servableStates,
    servableStates,
  };
};

export const previewPaymentResult = () => ({
  gateway: "preview",
  payment_data: { simulated: true },
});

const responseValues = (value) => Array.isArray(value) ? value : value == null ? [] : [value];
const choiceId = (value) => String(
  typeof value === "object"
    ? value.option_id || value.product_id || value.value || value.id
    : value
);

const displayValue = (...values) => {
  for (const value of values) {
    const normalized = String(value || "").trim();
    if (normalized) return normalized;
  }
  return "";
};

const productDoseLabel = (product) => displayValue(
  product?.dose_label,
  product?.dose,
  product?.doseLabel,
  product?.dose_mapping_label,
);

const productRegimen = (product) => displayValue(
  product?.regimen,
  product?.titration_category,
  product?.regimen_name,
  product?.regimenName,
);

const patientDisplayFields = (product) => {
  const dose = productDoseLabel(product);
  const regimen = productRegimen(product);
  return {
    ...(dose ? { dose_label: dose, dose } : {}),
    ...(regimen ? { regimen, titration_category: regimen } : {}),
  };
};

export const buildPreviewOrderSummary = (questionnaire, responses) => {
  const itemsBySelection = new Map();
  (questionnaire?.questions || [])
    .filter((question) => question.question_type === "product_selection")
    .forEach((question) => {
      const choices = question.answer_choices || question.checkout_config?.products || [];
      responseValues(responses?.[question.id]).forEach((selection) => {
        const selectedId = choiceId(selection);
        const choice = choices.find((candidate) => [candidate.option_id, candidate.product_id, candidate.value, candidate.id]
          .some((candidateId) => String(candidateId) === selectedId));
        if (!choice) return;
        const quantity = Number(selection?.quantity || choice.quantity || 1);
        const daysSupply = Number(
          selection?.days_supply
          || choice.rx_days_supply
          || choice.days_supply
          || choice.default_supply_duration
          || 0,
        );
        const authoredDays = Number(
          choice.rx_days_supply || choice.days_supply || choice.default_supply_duration || 0,
        );
        const configuredPrice = choice.duration_prices?.[String(daysSupply)];
        const priceValue = configuredPrice != null
          ? configuredPrice
          : (daysSupply === 0 || daysSupply === authoredDays)
            ? choice.unit_price ?? choice.price
            : null;
        if (priceValue == null) return;
        const price = Number(priceValue);
        const selectionKey = [
          choice.program_id || question.program_id || question.id,
          choice.option_id || choice.product_id || choice.id,
        ].map(String).join(":");
        itemsBySelection.set(selectionKey, {
          id: choice.option_id || choice.product_id,
          product_id: choice.product_id,
          name: choice.name || choice.label || "Treatment product",
          ...patientDisplayFields(choice),
          description: choice.description || "",
          quantity,
          price,
          days_supply: daysSupply,
          ...(Number(choice.refills || 0) > 0 ? { refills: Number(choice.refills) } : {}),
        });
      });
    });
  const items = [...itemsBySelection.values()];
  const amount = items.reduce((total, item) => total + (item.price * item.quantity), 0);
  return { items, amount: amount.toFixed(2), preview: true };
};

export const buildPreviewTreatments = (questionnaire, responses) => {
  const selectedPrograms = new Set();
  (questionnaire?.questions || [])
    .filter((question) => question.question_type === "product_selection")
    .forEach((question) => {
      if (responseValues(responses?.[question.id]).length && question.program_id) {
        selectedPrograms.add(String(question.program_id));
      }
    });
  return (questionnaire?.program_labs || [])
    .filter((program) => selectedPrograms.has(String(program.program_id)))
    .map((program) => ({
      program_id: String(program.program_id),
      program_name: program.program_name,
      labs: program.labs || [],
    }));
};

const patientFinancials = (value) => (
  value?.financials?.patient && typeof value.financials.patient === "object"
    ? { ...value.financials.patient }
    : {}
);

const normalizeSupply = (supply) => ({
  id: supply.option_id || supply.product_id || supply.id,
  product_id: supply.product_id,
  name: supply.name || supply.product_name || "Treatment supply",
  quantity: Number(supply.quantity || 1),
  price: supply.unit_price
    ?? supply.patient_price
    ?? supply.financials?.patient?.patient_fee
    ?? "0.00",
  item_subtotal: supply.item_subtotal
    ?? supply.patient?.item_subtotal
    ?? supply.financials?.patient?.item_subtotal
    ?? "0.00",
  shipping_fee: supply.shipping_total
    ?? supply.shipping_fee
    ?? supply.patient?.shipping_fee
    ?? supply.financials?.patient?.shipping_fee
    ?? "0.00",
  line_total: supply.line_total
    ?? supply.patient?.line_total
    ?? supply.financials?.patient?.line_total
    ?? "0.00",
  is_supply: true,
  is_included: Boolean(supply.is_included),
  financials: patientFinancials(supply),
});

const normalizeProduct = (product) => ({
  ...(() => {
    const linkedSupplies = (product.linked_supplies || []).map(normalizeSupply);
    const itemSubtotal = Number(product.item_subtotal ?? 0);
    const linkedSupplySubtotal = linkedSupplies.reduce(
      (total, supply) => total + Number(supply.item_subtotal || 0),
      0,
    );
    const medicationSubtotal = product.financials?.patient?.medication_subtotal
      ?? (linkedSupplies.length
        ? Math.max(0, itemSubtotal - linkedSupplySubtotal).toFixed(2)
        : product.item_subtotal ?? "0.00");
    return {
      medication_subtotal: medicationSubtotal,
      linked_supplies: linkedSupplies,
    };
  })(),
  id: product.option_id || product.product_id,
  option_id: product.option_id,
  product_id: product.product_id,
  name: product.name || product.product_name || "Treatment product",
  dose_label: productDoseLabel(product),
  dose: productDoseLabel(product),
  regimen: productRegimen(product),
  titration_category: productRegimen(product),
  description: product.description || "",
  product_role: product.product_role || "primary_choice",
  choice_group: product.choice_group || "",
  quantity: Number(product.quantity || 1),
  days_supply: Number(product.days_supply || 0) || null,
  refills: Math.max(0, Number(product.refills || 0)),
  price: product.unit_price ?? "0.00",
  item_subtotal: product.item_subtotal ?? "0.00",
  shipping_fee: product.shipping_total ?? "0.00",
  discount: product.discount ?? "0.00",
  line_total: product.line_total ?? "0.00",
  savings: product.savings ?? "0.00",
  image: product.product_image || "",
  financials: patientFinancials(product),
});

const normalizeLab = (lab) => ({
  id: lab.requirement_id || lab.assignment_id || lab.panel_id,
  requirement_id: lab.requirement_id,
  panel_id: lab.panel_id,
  assignment_id: lab.assignment_id,
  name: lab.name || "Lab panel",
  lab_provider: lab.lab_provider || lab.lab_name || lab.provider_network || "",
  lab_name: lab.lab_name || "",
  provider_network: lab.provider_network || "",
  marker_count: Number(lab.marker_count || 0),
  markers: Array.isArray(lab.markers) ? lab.markers : [],
  collection_method: lab.collection_method || "",
  sample_type: lab.sample_type || lab.specimen || "",
  required: lab.required !== false,
  quantity: Number(lab.quantity || 1),
  patient_price: lab.patient_price
    ?? lab.financials?.patient?.patient_fee
    ?? "0.00",
  shipping_fee: lab.shipping_fee
    ?? lab.financials?.patient?.shipping_fee
    ?? "0.00",
  discount: lab.financials?.patient?.discount ?? "0.00",
  line_total: lab.line_total
    ?? lab.financials?.patient?.line_total
    ?? "0.00",
  financials: patientFinancials(lab),
});

const normalizeTreatment = (treatment) => {
  const productOptions = Array.isArray(treatment.product_options)
    ? treatment.product_options
    : [];
  const products = (treatment.products || []).map((product) => {
    const option = productOptions.find(
      (candidate) => product.option_id
        && String(candidate.option_id) === String(product.option_id),
    );
    return normalizeProduct({
      ...option,
      ...product,
      product_image: product.product_image || option?.product_image || "",
    });
  });
  const labs = (treatment.labs || []).map(normalizeLab);
  const patient = patientFinancials(treatment);
  return {
    treatment_type_id: treatment.treatment_type_id,
    treatment_type_key: treatment.treatment_type_key,
    program_id: treatment.program_id,
    name: treatment.treatment_type_name
      || treatment.program_name
      || treatment.program_snapshot?.name
      || "Treatment",
    phase: treatment.phase || treatment.program_snapshot?.phase || "",
    visit_type: treatment.visit_type || "",
    products,
    product_options: productOptions,
    labs,
    product_subtotal: treatment.treatment_subtotal
      ?? patient.product_subtotal
      ?? "0.00",
    product_shipping: treatment.treatment_shipping
      ?? patient.product_shipping
      ?? "0.00",
    product_discount: treatment.treatment_discount
      ?? patient.product_discount
      ?? "0.00",
    product_total: treatment.treatment_total
      ?? patient.product_total
      ?? "0.00",
    lab_total: treatment.lab_total ?? patient.lab_total ?? "0.00",
    total: patient.combined_total
      ?? String(
        Number(treatment.treatment_total || 0)
        + Number(treatment.lab_total || 0),
      ),
  };
};

export const buildLiveOrderSummary = (checkout) => {
  const treatments = (checkout?.treatments || []).map(normalizeTreatment);
  const items = treatments.flatMap((treatment) => (
    treatment.products.flatMap((product) => [
      product,
      ...product.linked_supplies,
    ])
  ));
  return {
    currency: checkout?.currency || "USD",
    release: checkout?.release || null,
    quote: checkout?.quote || null,
    submission_id: checkout?.submission_id || null,
    pricing: checkout?.pricing || {},
    treatments,
    items,
    labs: treatments.flatMap((treatment) => treatment.labs),
    amount: checkout?.grand_total || "0.00",
    grand_total: checkout?.grand_total || "0.00",
    // Server-owned cart totals. Passed through untouched: with more than one
    // treatment a browser-side sum is a second financial authority.
    cart: checkout?.summary || null,
    preview: checkout?.mode === "preview",
  };
};

export const resolveTreatmentOrderSummary = (checkout) => {
  if (checkout?.order_summary) return checkout.order_summary;
  if (!Array.isArray(checkout?.treatments)) return null;
  return buildLiveOrderSummary(checkout);
};
